import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Info, Pencil, Percent, Plus } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDelConcepto } from './PanelDelConcepto';
import type { ConceptoDeRetencion, Impuesto, ListaDeConceptos } from './contrato';

function enFecha(iso: string | undefined): string {
  if (!iso) return '—';
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

export function PaginaConceptosDeRetencion() {
  const [texto, setTexto] = useState('');
  const [impuesto, setImpuesto] = useState<Impuesto | ''>('');
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [editando, setEditando] = useState<ConceptoDeRetencion | null>(null);
  const [creando, setCreando] = useState(false);
  const [cerrando, setCerrando] = useState<ConceptoDeRetencion | null>(null);
  const [ultimoDia, setUltimoDia] = useState('');

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('retenciones.gestionar');

  const buscado = useValorDiferido(texto.trim());

  const listado = useQuery({
    queryKey: ['conceptos-retencion', buscado, impuesto, soloVigentes, pagina, tamanio],
    queryFn: () =>
      api.get<ListaDeConceptos>('/retenciones/conceptos', {
        query: { texto: buscado, impuesto, soloVigentes: String(soloVigentes), pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const cerrar = useMutation({
    mutationFn: () =>
      api.post<ConceptoDeRetencion>(`/retenciones/conceptos/${cerrando?.id}/cerrar`, {
        vigenteHasta: ultimoDia,
      }),
    onSuccess: () => {
      notificar.exito('Concepto cerrado', 'Deja de aplicarse tras esa fecha');
      setCerrando(null);
      setUltimoDia('');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['conceptos-retencion'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo cerrar'),
  });

  const columnas: Columna<ConceptoDeRetencion>[] = [
    {
      clave: 'codigo',
      encabezado: 'Código',
      clase: 'w-20',
      contenido: (c) => (
        <span className="importe text-xs font-semibold text-armazon-700">{c.codigo}</span>
      ),
    },
    {
      clave: 'descripcion',
      encabezado: 'Concepto',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block text-xs uppercase text-armazon-800">{c.descripcion}</span>
          <span className="block text-[10px] uppercase text-armazon-400">{c.impuestoEtiqueta}</span>
        </span>
      ),
    },
    {
      clave: 'porcentaje',
      encabezado: 'Porcentaje',
      alinear: 'derecha',
      clase: 'w-36',
      contenido: (c) =>
        c.seAplicaSolo ? (
          <span className="importe text-xs font-bold text-armazon-900">
            {Number(c.porcentaje).toLocaleString('es-EC', { minimumFractionDigits: 2 })} %
          </span>
        ) : (

          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">
            <Info className="size-3" aria-hidden />
            {c.notaPorcentaje}
          </span>
        ),
    },
    {
      clave: 'vigencia',
      encabezado: 'Vigencia',
      alinear: 'centro',
      clase: 'w-44',
      contenido: (c) => (
        <span className="block text-[11px] text-armazon-600">
          {enFecha(c.vigenteDesde)}
          {c.vigenteHasta ? (
            <span className="block text-[10px] uppercase text-armazon-400">
              hasta {enFecha(c.vigenteHasta)}
            </span>
          ) : (
            <span className="block text-[10px] font-semibold uppercase text-marca-700">Vigente</span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-24',
      contenido: (c) =>
        puedeGestionar ? (
          <span className="flex justify-end gap-1">
            <BotonDeFila icono={Pencil} titulo="Editar" alPulsar={() => setEditando(c)} />
            {!c.vigenteHasta && (
              <BotonDeFila
                icono={CalendarOff}
                titulo="Dejar de aplicarlo"
                alPulsar={() => setCerrando(c)}
              />
            )}
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Percent}
        color="indigo"
        titulo="Conceptos de retención"
        subtitulo="Qué se retiene y cuánto · con la vigencia de cada resolución del SRI"
        acciones={
          puedeGestionar && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-4" aria-hidden />
              Nuevo concepto
            </button>
          )
        }
      />

      <Tabla
        datos={listado.data}
        cargando={listado.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="conceptos"
        iconoVacio={Percent}
        mensajeVacio="Ningún concepto coincide"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={texto}
              alCambiar={(v) => {
                setTexto(v);
                setPagina(0);
              }}
              marcador="Código o descripción…"
              className="w-72"
            />
            <select
              value={impuesto}
              onChange={(e) => {
                setImpuesto(e.target.value as Impuesto | '');
                setPagina(0);
              }}
              aria-label="Qué impuesto"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none focus:border-marca-400"
            >
              <option value="">LOS DOS IMPUESTOS</option>
              <option value="1">IMPUESTO A LA RENTA</option>
              <option value="2">IVA</option>
            </select>
            <label className="flex items-center gap-2 text-[11px] font-semibold uppercase text-armazon-600">
              <input
                type="checkbox"
                checked={soloVigentes}
                onChange={(e) => {
                  setSoloVigentes(e.target.checked);
                  setPagina(0);
                }}
                className="size-4 rounded border-armazon-300"
              />
              Solo vigentes
            </label>
          </span>
        }
      />

      {(creando || editando) && (
        <PanelDelConcepto
          concepto={editando}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
          alGuardar={() => {
            setCreando(false);
            setEditando(null);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['conceptos-retencion'] });
          }}
        />
      )}

      {cerrando && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-armazon-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-sm font-bold uppercase text-armazon-900">
              Dejar de aplicar «{cerrando.codigo}»
            </h2>
            <p className="mt-2 text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
              No se borra: se cierra. Las retenciones ya emitidas con este concepto conservan su
              porcentaje y tienen que poder explicarse. A partir del día siguiente deja de
              ofrecerse.
            </p>
            <label className="mt-4 block text-[10px] font-bold uppercase text-armazon-600">
              Último día en que se aplica
              <input
                type="date"
                value={ultimoDia}
                min={cerrando.vigenteDesde}
                onChange={(e) => setUltimoDia(e.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-armazon-200 px-3 text-sm outline-none focus:border-marca-400"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCerrando(null);
                  setUltimoDia('');
                }}
                className="inline-flex h-10 items-center rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
              >
                Dejarlo
              </button>
              <button
                type="button"
                disabled={ultimoDia === '' || cerrar.isPending}
                onClick={() => cerrar.mutate()}
                className="inline-flex h-10 items-center rounded-lg bg-armazon-800 px-4 text-xs font-bold uppercase text-white hover:bg-armazon-900 disabled:opacity-40"
              >
                Cerrar la vigencia
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
